import { MsalProvider } from "@azure/msal-react";
import { AppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { useEffect, useRef } from "react";
import { RouterProvider } from "react-router";
import { Flex, toast } from "@bdo/cornerstone-ui";
import { useGetSettingsQuery } from "@/app/settingsApi";
import {
  useAppDispatch,
  useAppInsights,
  useClarity,
  useConfigureMsal,
  useVersionCheck,
} from "@/hooks";
import {
  AuthenticatedProvider,
  IdleTimerProvider,
  SignalRProvider,
} from "@/providers";
import { ErrorBoundary } from "@/components";
import { reactPlugin, router } from "@/config";

function App() {
  const dispatch = useAppDispatch();
  const { isLoading } = useGetSettingsQuery();
  const { updateAvailable, reload } = useVersionCheck();
  const refreshPromptShown = useRef(false);

  useAppInsights();
  useClarity();
  const { msalInstance } = useConfigureMsal();

  useEffect(() => {
    if (!updateAvailable || refreshPromptShown.current) {
      return;
    }

    refreshPromptShown.current = true;

    toast.info({
      title: "Update available",
      message: "A new version is available. Please refresh to continue.",
      duration: 0,
    });

    const shouldReload = globalThis.confirm(
      "A new version of EVA is available. Reload now?",
    );

    if (shouldReload) {
      reload();
    }
  }, [dispatch, reload, updateAvailable]);

  if (isLoading) {
    return (
      <Flex direction="column">
        <div>Loading settings...</div>
      </Flex>
    );
  }

  if (!msalInstance) {
    return (
      <Flex direction="column">
        <div>Error: MSAL instance is not configured.</div>
      </Flex>
    );
  }

  return (
    <AppInsightsContext.Provider value={reactPlugin}>
      <MsalProvider instance={msalInstance}>
        <AuthenticatedProvider>
          <SignalRProvider>
            <IdleTimerProvider>
              <ErrorBoundary>
                <RouterProvider router={router} />
              </ErrorBoundary>
            </IdleTimerProvider>
          </SignalRProvider>
        </AuthenticatedProvider>
      </MsalProvider>
    </AppInsightsContext.Provider>
  );
}

export default App;
